import { useCallback, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const BASE = '/api/v1/recruitee';

export default function useRecruitee() {
  const authCtx = useContext(AuthContext);
  const { sendRequest, isLoading, error, setError } = useHttp();

  const buildHeaders = useCallback(
    () => ({ 'Content-Type': 'application/json', ...authCtx.getAuthHeaders() }),
    [authCtx]
  );

  const safe = useCallback(async (request, errorMessage) => {
    try {
      return await request();
    } catch (err) {
      toast.error(errorMessage || 'Fehler beim Laden der Recruitee-Daten');
      throw err;
    }
  }, []);

  const listJobs = useCallback(
    (params = {}) =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/jobs`,
            method: 'GET',
            headers: buildHeaders(),
            params,
          }),
        'Fehler beim Laden der Recruitee-Jobs'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const listMappings = useCallback(
    (params = {}) =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/mappings`,
            method: 'GET',
            headers: buildHeaders(),
            params,
          }),
        'Fehler beim Laden der Recruitee-Mappings'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const createMapping = useCallback(
    payload =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/mappings`,
            method: 'POST',
            headers: buildHeaders(),
            body: payload,
          }),
        'Fehler beim Erstellen des Recruitee-Mappings'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const updateMapping = useCallback(
    (mappingId, payload) =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/mappings/${mappingId}`,
            method: 'PATCH',
            headers: buildHeaders(),
            body: payload,
          }),
        'Fehler beim Speichern des Recruitee-Mappings'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const deleteMapping = useCallback(
    mappingId =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/mappings/${mappingId}`,
            method: 'DELETE',
            headers: buildHeaders(),
          }),
        'Fehler beim Löschen des Recruitee-Mappings'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const sync = useCallback(
    payload =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/sync`,
            method: 'POST',
            headers: buildHeaders(),
            body: payload || {},
          }),
        'Fehler beim Synchronisieren der Recruitee-Bewerber'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const setApplicantHidden = useCallback(
    (applicantId, hidden) =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/applicants/${applicantId}/hidden`,
            method: 'PATCH',
            headers: buildHeaders(),
            body: { hidden },
          }),
        hidden
          ? 'Fehler beim Ausblenden des Bewerbers'
          : 'Fehler beim Einblenden des Bewerbers'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const listApplicants = useCallback(
    (params = {}) =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/applicants`,
            method: 'GET',
            headers: buildHeaders(),
            params,
          }),
        'Fehler beim Laden der Recruitee-Bewerber'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const getApplicantStats = useCallback(
    (params = {}) =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/applicants/stats`,
            method: 'GET',
            headers: buildHeaders(),
            params,
          }),
        'Fehler beim Laden der Recruitee-Auswertung'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const getOverdueCount = useCallback(
    (params = {}) =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/applicants/overdue/count`,
            method: 'GET',
            headers: buildHeaders(),
            params,
          }),
        'Fehler beim Laden der überfälligen Recruitee-Bewerber'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const getOverdueApplicantsForWelcome = useCallback(
    () =>
      sendRequest({
        url: `${BASE}/applicants`,
        method: 'GET',
        headers: buildHeaders(),
        params: { overdue: true, page: 1, limit: 5 },
      }),
    [buildHeaders, sendRequest]
  );

  return useMemo(
    () => ({
      isLoading,
      error,
      setError,
      listJobs,
      listMappings,
      createMapping,
      updateMapping,
      deleteMapping,
      sync,
      setApplicantHidden,
      listApplicants,
      getApplicantStats,
      getOverdueCount,
      getOverdueApplicantsForWelcome,
    }),
    [
      isLoading,
      error,
      setError,
      listJobs,
      listMappings,
      createMapping,
      updateMapping,
      deleteMapping,
      sync,
      setApplicantHidden,
      listApplicants,
      getApplicantStats,
      getOverdueCount,
      getOverdueApplicantsForWelcome,
    ]
  );
}
